"use client"

import Link from "next/link"
import { motion } from "framer-motion"
import { 
  BookOpen, 
  Target, 
  Calendar, 
  BarChart3, 
  FileText, 
  Users,
  ArrowRight,
  Clock
} from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { useUser } from "@stackframe/stack"

const quickAccessItems = [
  {
    icon: <Target className="h-8 w-8 text-orange-500" />,
    title: "智能题库",
    description: "海量真题模拟，智能推荐练习",
    href: "/questions",
    color: "orange",
    features: ["历年真题", "模拟考试", "错题回顾"]
  },
  {
    icon: <Calendar className="h-8 w-8 text-blue-500" />,
    title: "学习计划",
    description: "个性化学习路径，科学备考安排",
    href: "/study-plan",
    color: "blue",
    features: ["智能规划", "进度跟踪", "提醒通知"]
  },
  {
    icon: <BookOpen className="h-8 w-8 text-green-500" />,
    title: "科目资料",
    description: "权威教材资源，系统知识梳理",
    href: "/subjects",
    color: "green",
    features: ["官方教材", "知识点梳理", "重点标注"]
  },
  {
    icon: <BarChart3 className="h-8 w-8 text-purple-500" />,
    title: "学习统计",
    description: "数据驱动学习，精准分析弱项",
    href: "/profile",
    color: "purple",
    features: ["能力分析", "学习报告", "成长轨迹"]
  },
  {
    icon: <Clock className="h-8 w-8 text-red-500" />,
    title: "每日练习",
    description: "每日精选题目，保持学习节奏",
    href: "/questions/daily",
    color: "red",
    features: ["每日更新", "难度适配", "坚持打卡"]
  },
  {
    icon: <FileText className="h-8 w-8 text-indigo-500" />,
    title: "模拟考试",
    description: "真实考试环境，全面能力检测",
    href: "/questions/practice",
    color: "indigo",
    features: ["限时考试", "成绩分析", "排名对比"]
  }
]

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring" as const,
      stiffness: 100,
      damping: 10,
    },
  },
}

export const QuickAccessSection = () => {
  const user = useUser()

  return (
    <section className="w-full py-20 md:py-28 bg-slate-50 dark:bg-slate-900">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {user ? "快速开始学习" : "核心学习功能"}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {user 
              ? "选择您需要的学习功能，继续您的备考之旅" 
              : "专业的学习工具，助您高效备考软考高级认证"
            }
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quickAccessItems.map((item, index) => (
            <motion.div
              key={item.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={cardVariants}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="h-full group hover:shadow-xl transition-all duration-300 hover:scale-105 border-0 shadow-md">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {item.icon}
                      <CardTitle className="text-xl">{item.title}</CardTitle>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <p className="text-muted-foreground text-sm mt-2">
                    {item.description}
                  </p>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2 mb-4">
                    {item.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className={`w-1.5 h-1.5 rounded-full bg-${item.color}-500`} />
                        {feature}
                      </div>
                    ))}
                  </div>
                  <Button asChild className="w-full" variant="outline">
                    <Link href={item.href}>
                      {user ? "立即使用" : "了解更多"}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {!user && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="text-center mt-12"
          >
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">准备开始您的学习之旅？</h3>
              <p className="text-orange-100 mb-6 max-w-2xl mx-auto">
                注册账号，解锁所有学习功能，享受个性化的学习体验
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" asChild>
                  <Link href="/handler/signup">
                    免费注册 <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white hover:text-orange-500 backdrop-blur-sm" asChild>
                  <Link href="/handler/login">已有账号登录</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  )
}
